import type { DocPage } from '../types'
import { media } from '../media'

export const account: DocPage = {
  id: 'account',
  title: 'Account & subscription',
  description:
    'How X-Translator signs you in with SEPM USA, switches organizations, unlocks formats and features, and what to do when messages block access.',
  category: 'Getting started',
  sections: [
    { id: 'signin', title: 'Sign in' },
    { id: 'oauth', title: 'Authorize the application' },
    { id: 'orgs', title: 'Organizations' },
    { id: 'subscription', title: 'Subscription and seats' },
    { id: 'entitlements', title: 'Formats and features' },
    { id: 'profile', title: 'Profile menu' },
    { id: 'messages', title: 'Troubleshooting messages' },
    { id: 'account-help', title: 'If something is locked' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'signin',
      text: 'Sign in',
    },
    {
      type: 'paragraph',
      text: 'X-Translator does not store its own username and password list. Sign-in opens your SEPM account (USA at accounts.sepm.ch). You can use the in-app browser window (Sign in — SEPM X-Translator) or choose Sign in using system browser instead.',
    },
    {
      type: 'figure',
      src: media.signInScreen1,
      alt: 'X-Translator sign-in card',
      caption:
        'Figure: Login card on SEPM X-Translator — Sign In starts the SEPM account flow.',
      size: 'compact',
    },
    {
      type: 'figure',
      src: media.signInScreen2,
      alt: 'SEPM account sign-in screen',
      caption:
        'Figure: SEPM account sign-in (email/password, social login, or MFA when enabled).',
      size: 'compact',
    },
    {
      type: 'steps',
      items: [
        'On the login card, click Sign In (or use the system browser option).',
        'Complete SEPM login (email/password, social login, or MFA if enabled).',
        'If prompted, Authorise / allow the SEPM X-Translator application.',
        'When you see Sign-in successful, return to X-Translator.',
        'The app stores the session securely on your machine and loads the workspace.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'oauth',
      text: 'Authorize the application',
    },
    {
      type: 'paragraph',
      text: 'The first time (or when consent is required), SEPM asks you to authorise X-Translator to access your account on behalf of the desktop app. Approve to continue; denying cancels sign-in.',
    },
    {
      type: 'figure',
      src: media.authoriseApplication,
      alt: 'Authorise application consent screen',
      caption:
        'Figure: Authorise application — grant SEPM X-Translator access to continue sign-in.',
      size: 'compact',
    },
    {
      type: 'figure',
      src: media.authorizationSuccessful,
      alt: 'Authorization successful screen',
      caption:
        'Figure: Authorization successful — the OAuth step completed; continue or return to the app as prompted.',
      size: 'compact',
    },
    {
      type: 'figure',
      src: media.signInSuccessful,
      alt: 'Sign-in successful confirmation',
      caption:
        'Figure: Sign-in successful — return to X-Translator to load your workspace and organization entitlements.',
      size: 'compact',
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'What happens behind the scenes',
      text: 'X-Translator starts a secure OAuth sign-in (PKCE), receives a one-time code on your machine, exchanges it through the local desktop API, and loads your profile plus organization entitlements for product X-Translator.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'orgs',
      text: 'Organizations',
    },
    {
      type: 'paragraph',
      text: 'Access is always tied to an organization. After login, X-Translator selects a default organization (preferring one that already has X-Translator features/formats). You can switch organizations from the profile menu.',
    },
    {
      type: 'table',
      headers: ['Situation', 'What you see'],
      rows: [
        [
          'Active org has no X-Translator access',
          'Switch organization / No access for current organization',
        ],
        [
          'Org details missing from the session',
          'Organization details unavailable — Use Refresh account in the profile menu, or sign out and sign in again.',
        ],
        ['No organizations on the account', 'Empty Organizations list / No organization'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'subscription',
      text: 'Subscription and seats',
    },
    {
      type: 'paragraph',
      text: 'Plans, seats, billing, and invitations are managed in SEPM My Account — not inside X-Translator. To use the desktop app for an organization, you typically need:',
    },
    {
      type: 'list',
      ordered: true,
      items: [
        'Membership in that organization.',
        'Assignment to a LIVE subscription that includes X-Translator.',
        'Enough licensed seats remaining when your admin assigns you.',
      ],
    },
    {
      type: 'callout',
      variant: 'warning',
      title: 'LIVE subscriptions only',
      text: 'Only LIVE subscriptions grant X-Translator features and formats. Trial, cancelled, or expired subscriptions do not unlock the product in the entitlement calculation.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'entitlements',
      text: 'Formats and features',
    },
    {
      type: 'paragraph',
      text: 'Your organization’s plan and addons unlock format keys (for example postgis-source, cad-target) and feature keys. Items you are not entitled to appear disabled, often with a lock or crown indicator.',
    },
    {
      type: 'table',
      headers: ['UI area', 'Typical gate'],
      rows: [
        ['Format picker (source/target)', 'Format entitlement (…-source / …-target)'],
        ['Transfer History', 'view-transfer-history'],
        ['Create Custom Config', 'create_custom_config'],
        ['View coordinate conversion', 'view-coordinate-conversion'],
        ['Change coordinate conversion', 'change-coordinate-conversion'],
        ['Global log settings', 'change-log-settings'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'profile',
      text: 'Profile menu',
    },
    {
      type: 'list',
      items: [
        'Your name, email, and optional profile picture.',
        'Active organization (or No organization).',
        'Organizations list — switch with a checkmark on the active org.',
        'Refresh Account — re-fetch profile and entitlements from SEPM.',
        'Sign out — clears the local session and returns to the login card.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'messages',
      text: 'Troubleshooting messages',
    },
    {
      type: 'table',
      headers: ['Message / UI', 'Meaning', 'What to do'],
      rows: [
        [
          'Sign-in was cancelled or incomplete.',
          'OAuth window closed early or timed out.',
          'Retry Sign In; finish within a few minutes; try system browser.',
        ],
        [
          'Sign-in successful!',
          'OAuth completed.',
          'Return focus to X-Translator; wait for workspace load.',
        ],
        [
          'Switch organization / No access for current organization',
          'Active org lacks X-Translator entitlements.',
          'Pick another org; ask admin for a LIVE seat; Refresh Account.',
        ],
        [
          'Organization details unavailable',
          'Profile/org payload incomplete.',
          'Use Refresh account… or sign out and sign in again.',
        ],
        [
          'Access restricted',
          'Generic feature gate dialog.',
          'Contact admin for the specific feature entitlement.',
        ],
        [
          'You don\'t have access to view the coordinates conversion, contact the admin',
          'Missing view-coordinate-conversion.',
          'Ask admin; no in-app billing UI.',
        ],
        [
          'You don\'t have access to change the coordinates conversion, contact the admin',
          'Missing change-coordinate-conversion.',
          'Ask admin for change rights.',
        ],
        [
          'You don\'t have access to change log settings, contact the admin',
          'Missing change-log-settings.',
          'Ask admin; you can still read the bottom logger.',
        ],
        [
          'Invalid or expired access token',
          'Session token no longer accepted.',
          'Refresh Account; if it fails, Sign out and Sign In.',
        ],
        [
          'Login code missing / Authorization Failed',
          'Browser OAuth did not return a usable code.',
          'Do not close the browser early; retry with the correct account.',
        ],
        [
          'Create Custom Config locked',
          'Missing create_custom_config.',
          'Admin enables feature; Refresh Account; meanwhile use New each time.',
        ],
        [
          'Transfer History crown/lock',
          'Missing view-transfer-history.',
          'Admin enables feature; use bottom logger for immediate diagnostics.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'account-help',
      text: 'If something is locked',
    },
    {
      type: 'list',
      items: [
        'Cannot sign in — try the system browser; finish login within a few minutes; retry if the authorization state expired.',
        'Signed in but locked — switch organization; ask an admin to assign a LIVE X-Translator seat; then Refresh Account.',
        'Missing format or feature — your plan/addons do not include it; contact your organization administrator.',
        'Need to change plan or seats — use SEPM My Account, not X-Translator.',
      ],
    },
  ],
}
